import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';

import { SubsidiaryPicker } from '~/features/platform/components/subsidiary-picker';
import {
  SubsidiaryScopeContext,
  useResolvedSubsidiaryScope,
} from '~/features/platform/hooks/use-subsidiary-scope';
import { useAuthStore } from '~/store/auth.store';
import { cn } from '~/utils/cn';

/**
 * One tab in a subsidiary-scoped section's tab bar. Labels arrive resolved so each
 * section keeps its own i18n namespace.
 */
export interface SubsidiaryScopedNavItem {
  to: string;
  /** Already-translated tab label. */
  label: string;
  /** Leading icon shown in the tab. */
  icon: LucideIcon;
  /** Exact-match the active state instead of prefix-matching. Use for an index
   *  route so it doesn't stay active on its children. */
  exact?: boolean;
  /** Only show to super admins (the route is guarded separately). */
  superAdminOnly?: boolean;
  /** Already-translated "All subsidiaries" label: this tab reads every subsidiary while none is
   *  picked, so the picker offers that choice and names it instead of the active tenant. */
  allSubsidiariesLabel?: string;
}

/** Whether `item` is the tab for `pathname`. */
function isNavItemActive(pathname: string, item: SubsidiaryScopedNavItem): boolean {
  return item.exact
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(item.to + '/');
}

interface SubsidiaryScopedTabsLayoutProps {
  title: string;
  subtitle: string;
  navAriaLabel: string;
  navItems: SubsidiaryScopedNavItem[];
}

/**
 * Shell for a subsidiary-scoped section (Integrations, Explorer) — page header with
 * the shared subsidiary picker, a horizontal tab bar, and the active child page below
 * it. Owns the `?sc=` contract so every section resolves and forwards scope identically.
 */
export function SubsidiaryScopedTabsLayout({
  title,
  subtitle,
  navAriaLabel,
  navItems,
}: SubsidiaryScopedTabsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  // Subsidiary scope is derived from the shared `?sc=` param (source of truth), falling back to
  // the operator's active tenant. One picker here drives every tab in the section. Resolution
  // lives in the hook so the standalone upload-delivery layout resolves `?sc=` identically — if
  // the two disagreed, opening that flow from a tab would silently switch subsidiary.
  //
  // `hasExplicitScope` = the operator actually picked a subsidiary they can reach. Tab links
  // forward the param ONLY then, otherwise every URL gains a redundant `?sc=<own-tenant>`.
  const { id: scopeId, name: scopeName, hasExplicitScope } = useResolvedSubsidiaryScope();

  /** Re-scope every tab by writing the shared `?sc=` param on the current route. */
  function onSelectSubsidiary(next: { id: string; name: string }) {
    void navigate({ to: '.', search: (prev) => ({ ...prev, sc: next.id }) });
  }

  /** Back to every subsidiary: drop `?sc=` (only offered on a tab that reads them all). */
  function onSelectAllSubsidiaries() {
    void navigate({ to: '.', search: (prev) => ({ ...prev, sc: undefined }) });
  }

  const visibleItems = navItems.filter((item) => !item.superAdminOnly || isSuperAdmin);
  const allLabel = visibleItems.find((item) =>
    isNavItemActive(location.pathname, item),
  )?.allSubsidiariesLabel;
  const showsAll = allLabel !== undefined && !hasExplicitScope;

  return (
    <div className="bg-gradient-page flex h-full flex-col gap-4 p-6 pt-5">
      {/* Compact section header — title + description on the left, the shared
          subsidiary picker pinned top-right; sits on the page gradient. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground text-xl leading-tight font-semibold">{title}</h1>
          <p className="text-muted-foreground mt-0.5 text-sm leading-5">{subtitle}</p>
        </div>
        <SubsidiaryPicker
          value={scopeId}
          valueLabel={showsAll ? allLabel : scopeName}
          onSelect={onSelectSubsidiary}
          {...(allLabel !== undefined
            ? {
                allOption: {
                  label: allLabel,
                  selected: !hasExplicitScope,
                  onSelect: onSelectAllSubsidiaries,
                },
              }
            : {})}
        />
      </div>

      {/* Single panel: the tab bar is the panel's header strip (bottom hairline),
          and the active child page renders directly below it. Tabs + content
          share one white surface so the underline rule aligns to the card edge;
          the tab bar stays pinned while the content scrolls. */}
      <div className="bg-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border shadow-sm">
        <nav
          aria-label={navAriaLabel}
          className="border-border/70 scrollbar-thin flex shrink-0 items-center gap-6 overflow-x-auto border-b px-6 pt-3.5"
        >
          {visibleItems.map((item) => (
            <SubsidiaryScopedNavLink
              key={item.to}
              item={item}
              {...(hasExplicitScope ? { sc: scopeId } : {})}
            />
          ))}
        </nav>

        {/* Provide the scope to every page; key by subsidiary id so switching it
            remounts the active page (resetting its connection/local state). */}
        <SubsidiaryScopeContext.Provider value={{ id: scopeId, name: scopeName }}>
          <div key={scopeId} className="min-h-0 flex-1 overflow-auto p-6">
            <Outlet />
          </div>
        </SubsidiaryScopeContext.Provider>
      </div>
    </div>
  );
}

/**
 * Single horizontal tab — icon + label with a brand-accent underline when active.
 *
 * `sc` is the section-wide subsidiary scope, forwarded onto the target route.
 * Without it the param is dropped on navigation (TanStack `Link` does not inherit
 * search params) and the layout silently falls back to the operator's own tenant —
 * i.e. switching tabs would reset the picker to the default subsidiary. Search is
 * REPLACED rather than merged from `prev` on purpose: `sc` is the only param that
 * belongs to the whole section, so page-local ones (e.g. Connections'
 * `?connectionId=`) must not leak sideways onto another tab.
 */
function SubsidiaryScopedNavLink({ item, sc }: { item: SubsidiaryScopedNavItem; sc?: string }) {
  const location = useLocation();

  const isActive = isNavItemActive(location.pathname, item);
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      search={sc ? { sc } : {}}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        '-mb-px flex shrink-0 cursor-pointer items-center gap-2 border-b-2 px-1 pb-2.5 text-sm font-medium whitespace-nowrap transition-colors',
        isActive
          ? 'border-primary text-primary'
          : 'text-muted-foreground hover:text-foreground border-transparent',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {item.label}
    </Link>
  );
}
